import {
  Dialog,
  DialogType,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import { ExperimentType, PullRequestChange } from "../../Types";
import {
  createAdoBranch,
  createAdoPullRequest,
  fetchAdoContent,
  pushToAdoBranch,
} from "../../Api/ado.api";
import { ARCHIVE_KEY } from "../../Shared/shared.consts";
import { useMemo, useState } from "react";
import { styles as sharedStyles } from "../../Shared/shared.styles";
import { useFetchPipelineId } from "../../Hooks/AdoHooks/UseFetchPipelineId";
import { useFetchPipelineRuns } from "../../Hooks/AdoHooks/UseFetchPipelineRuns";
import { useFetchApprovalLinks } from "../../Hooks/AdoHooks/UseFetchPipelineLinks";
import { useFetchEv2Status } from "../../Hooks/Ev2Hooks/UseFetchEv2Status";
import { parseRolloutType } from "../../Experiment/RolloutDetails/rolloutDetails.utils";
import { getExpTreatment } from "../../Shared/shared.utils";
import {
  ROLLBACK_TYPE,
  ROLLOUT_EXPERIMENT_ACTION,
} from "../../Experiment/RolloutDetails/RolloutDetailsTable/RolloutDetailsEv2Panel/ev2TreatmentStages.consts";
import ArchiveExperimentConfirmFinal from "./ArchiveExperimentConfirmFinal";
import ArchiveExperimentsConfirmRunning from "./ArchiveExperimentConfirmRunning";
import useAppInsights from "../../Hooks/UseAppInsights";
import { createBaseOperationTracker } from "../../Shared/AppInsights";

interface ArchiveExperimentConfirmationModalProps {
  /**
   * The experiment to be archived.
   */
  experiment: ExperimentType;
  /**
   * Callback function on successful archiving of the experiment.
   */
  onSuccess: (item: ExperimentType) => void;
  /**
   * Callback function to cancel the archiving process.
   */
  onCancel: () => void;
}

export interface EnvStatus {
  treatment: string;
  isRunning: boolean;
  startTime: string;
}

/**
 * Dialog component to confirm archiving an experiment. Notifies users that the experiment will
 * be archived, requiring an ADO pull request, and that archiving will remove the experiment
 * from UI management.
 */
const ArchiveExperimentConfirmationModal: React.FC<
  ArchiveExperimentConfirmationModalProps
> = ({ experiment, onSuccess, onCancel }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const stageType = "single";
  const configFileName = experiment.__filename;

  const { trackOperationEvent } = useAppInsights(
    "ArchiveExperimentConfirmationModal",
  );

  const { value: pipelineId, loading: loadingPipelineId } = useFetchPipelineId(
    stageType,
    setError,
  );

  const { value: pipelineRuns = [], loading: loadingRuns = true } =
    useFetchPipelineRuns(pipelineId, configFileName, setError);

  const { ev2PortalLinks, loadingEv2Links } = useFetchApprovalLinks(
    pipelineRuns,
    setError,
  );

  const { ev2StatusMap, ev2StatusLoading } = useFetchEv2Status(
    pipelineRuns,
    ev2PortalLinks,
    setError,
  );

  const isLoadingEv2Links = useMemo(
    () => Object.values(loadingEv2Links).some((link) => link),
    [loadingEv2Links],
  );
  const isLoadingEv2Status = useMemo(
    () => Object.values(ev2StatusLoading).some((status) => status),
    [ev2StatusLoading],
  );
  const loadingArchiveStatus =
    loadingPipelineId || loadingRuns || isLoadingEv2Links || isLoadingEv2Status;

  const handleOnSuccess = () => {
    onSuccess(experiment);
    onCancel();
  };

  const onContinue = () => {
    setShowFinalConfirm(true);
    trackOperationEvent({
      operation: "ConfirmArchive",
      function: "onContinue",
    });
  };

  const envStatuses = useMemo(() => {
    if (loadingArchiveStatus) {
      return {
        RC: null,
        MPAC: null,
        PROD: null,
      };
    }
    return pipelineRuns
      .sort((runA, runB) => runB.createdDate.localeCompare(runA.createdDate))
      .reduce(
        (statusMap: Record<string, EnvStatus | null>, pipeline) => {
          const { environment } = parseRolloutType(
            pipeline.templateParameters.RolloutAndProgressionType,
          );
          const pipelineId = pipeline.id;
          const statuses = ev2StatusMap[pipelineId] || [];
          const rolloutStatus = statuses.find(
            (status) => status.rolloutAction === ROLLOUT_EXPERIMENT_ACTION,
          );
          if (
            rolloutStatus &&
            rolloutStatus.status !== "Failed" &&
            // if succeeded but no link available, means it was a validation run
            rolloutStatus.links.experiment &&
            !statusMap[environment]
          ) {
            const expTreatment = getExpTreatment(rolloutStatus.stageName, true);
            return {
              ...statusMap,
              [environment]: {
                treatment: expTreatment,
                isRunning: expTreatment !== ROLLBACK_TYPE,
                startTime: rolloutStatus.startTime,
              },
            };
          }
          return statusMap;
        },
        {
          RC: null,
          MPAC: null,
          PROD: null,
        },
      );

    // eslint-disable-next-line react-hooks/exhaustive-deps --  only depends on loadingArchiveStatus
  }, [loadingArchiveStatus]);

  const hasRunningExperiments = useMemo(
    () =>
      Object.values(envStatuses).some((status) => status && status.isRunning),
    [envStatuses],
  );

  if (loadingArchiveStatus) {
    return (
      <Dialog
        hidden={false}
        onDismiss={onCancel}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Confirm Archive Experiment",
          isMultiline: true,
        }}
        modalProps={{
          isBlocking: true,
        }}
      >
        <Spinner
          size={SpinnerSize.medium}
          label="Verifying archive eligibility..."
          labelPosition="top"
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      hidden={false}
      onDismiss={onCancel}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Confirm Archive Experiment",
        subText: `Attempting to archive "${experiment.featureKey}"`,
        isMultiline: true,
      }}
      modalProps={{
        isBlocking: true,
      }}
    >
      {error && (
        <>
          <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
          <br />
        </>
      )}
      {hasRunningExperiments && !showFinalConfirm ? (
        <ArchiveExperimentsConfirmRunning
          experiment={experiment}
          statusMap={envStatuses}
          onContinue={onContinue}
          onCancel={onCancel}
        />
      ) : (
        <ArchiveExperimentConfirmFinal
          experiment={experiment}
          isLoading={isLoading}
          onArchive={() =>
            onArchive(experiment, setIsLoading, handleOnSuccess, setError)
          }
          onCancel={onCancel}
        />
      )}
      {isLoading && (
        <div className={sharedStyles.spinnerOverlay}>
          <Spinner
            size={SpinnerSize.large}
            label="Archiving experiment..."
            labelPosition="bottom"
          />
        </div>
      )}
    </Dialog>
  );
};

/**
 * Archive handler, creates a new branch, pushes the changes to rename the experiment file,
 * and creates a pull request for archiving the experiment.
 * @param experiment experiment to be archived
 * @param setIsLoading function to set loading state
 * @param onSuccess callback function on successful archiving
 * @param setError callback function on error during archiving
 */
const onArchive = async (
  experiment: ExperimentType,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  onSuccess: () => void,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
) => {
  const branchName = `archive-experiment-${experiment.featureKey}`;
  const extensionName = experiment.__extension;
  setIsLoading(true);
  const tracker = createBaseOperationTracker("Archive", "onArchive", {
    component: "ArchiveExperimentConfirmationModal",
    extensionName,
    experimentName: experiment.featureKey,
  });
  tracker.start({ branchName, experiment });
  try {
    await createAdoBranch(branchName, "main");
    tracker.step("BranchCreated", { branchName });

    const itemPath = experiment.__fullpath;
    const newItemPath = itemPath.replace(".json", `${ARCHIVE_KEY}.json`);

    // Fetch the current file content to preserve it during rename
    const fileContent = await fetchAdoContent(itemPath);
    // Convert the raw content back to JSON string for the rename operation
    const contentString =
      typeof fileContent.__raw === "string"
        ? fileContent.__raw
        : JSON.stringify(fileContent.__raw, null, 2);

    const changes: PullRequestChange[] = [
      {
        changeType: "rename",
        sourceServerItem: itemPath,
        itemPath: newItemPath,
        newContent: contentString,
        contentType: "rawtext",
      },
    ];

    const commitMessage = `[${ARCHIVE_KEY}] Experiment ${experiment.featureKey}`;

    await pushToAdoBranch({ branchName, commitMessage, changes });
    tracker.step("FilesPushed", { branchName, commitMessage });

    await createAdoPullRequest({
      sourceBranch: branchName,
      targetBranch: "main",
      title: `[${ARCHIVE_KEY}] Archive Experiment: ${experiment.featureKey}`,
      description: `[${ARCHIVE_KEY}] Experimentation UI Request to archive experiment: ${experiment.featureKey}. Once this PR is merged, the experiment will be archived and no longer manageable via the UI but will still be available in the repository`,
    });
    tracker.step("PullRequestCreated", { branchName });

    tracker.succeed();
  } catch (err) {
    tracker.fail(err instanceof Error ? err.message : String(err));
    setError("Error while archiving experiment: " + (err as string));
    console.error("Failed archive experiment:", err);
    return;
  } finally {
    setIsLoading(false);
  }
  onSuccess();
};

export default ArchiveExperimentConfirmationModal;
